import _ from 'lodash';
import {
  GET_USER_BY_OKTA_USER_ID,
  AUTH_ERROR,
  DEAUTH_USER,
  RESET_PASSWORD,
  CHANGE_PASSWORD,
  PUT_USER_AUTOASSIGN,
  AUTH_USER,
  SET_ACCESS_TOKEN_ERROR_MESSAGE,
  SET_CURRENT_USER_ROLES,
  SET_CURRENT_USER_GROUPS,
  UPDATE_PROFILE,
} from '../constants/actions';
import { formatInit, formatSuccess, formatError } from '../constants/endpoints';
import { SUCCESS, TIMEOUT_DURATION } from '../constants/general';
import { setupDatadogRumUserAndContext } from '../helpers/datadog-rum';

import * as pendoHelpers from '../helpers/pendo/pendo';

import {
  CHAT_ALL_INBOX,
  CASES_TAB,
  CHATS_TAB,
  CLAIMS_TAB,
  POLICY_TAB,
  MASSNOTIFICATIONS_TAB,
  OUTREACH_CLAIMS_MESSAGING,
  USER_MANAGEMENT_TAB,
  ANALYTICS_TAB,
  RESTRICT_SEND_MESSAGE,
  RESTRICT_CASE_ACTIONS,
  RESTRICT_CASE_EDITING,
  RESTRICT_CASE_ASSIGNMENT,
  RESTRICT_CHAT_PARTICIPANT_CONFIGURATION,
  RESTRICT_NOTES_EDITING,
  RESTRICT_SURVEY_UI,
  RESTRICT_TRANSLATE_EDITING,
  WEBAPP_CASE_CREATION,
  MULTI_PARTY_TEXTING,
} from '../constants/permissions';
import { FOCUSED_OPERATOR, VIEW_ONLY_OPERATOR } from '../constants/roles';

export const initialState = {
  error: false,
  user: {},
  inactivityTimeout: TIMEOUT_DURATION,
  permissions: [],
  retrievedUserInfo: null,
  passwordIsChanged: false,
  passwordResetted: false,
  pendingRequest: false,
  oktaUserId: null,
  logout: false,
  score: '',
  accessTokenErrorMessage: null,
};

const standardPermissions = [
  CHAT_ALL_INBOX, // Ability to view chat all inbox
];

const personas = {};
// user roles are not separated by _ characters
_.set(personas, FOCUSED_OPERATOR, {
  EXCLUDES: [
    CHAT_ALL_INBOX,
    CASES_TAB,
    CLAIMS_TAB,
    POLICY_TAB,
    MASSNOTIFICATIONS_TAB,
    OUTREACH_CLAIMS_MESSAGING,
    USER_MANAGEMENT_TAB,
    ANALYTICS_TAB,
  ],
});
_.set(personas, VIEW_ONLY_OPERATOR, {
  INCLUDES: [
    CASES_TAB,
    CHATS_TAB,
    CLAIMS_TAB,
    POLICY_TAB,
    RESTRICT_SEND_MESSAGE,
    RESTRICT_CASE_ACTIONS,
    RESTRICT_CASE_EDITING,
    RESTRICT_CASE_ASSIGNMENT,
    RESTRICT_CHAT_PARTICIPANT_CONFIGURATION,
    RESTRICT_SURVEY_UI,
    RESTRICT_NOTES_EDITING,
    RESTRICT_TRANSLATE_EDITING,
  ],
  EXCLUDES: [
    WEBAPP_CASE_CREATION,
    MULTI_PARTY_TEXTING,
    MASSNOTIFICATIONS_TAB,
    OUTREACH_CLAIMS_MESSAGING,
    ANALYTICS_TAB,
  ],
});

const availablePersonas = Object.keys(personas);

const applyPersonas = (permissions, roles) => {
  let updated = permissions;
  _.intersection(roles, availablePersonas).forEach((r) => {
    const { INCLUDES = [], EXCLUDES = [] } = personas[r];
    updated = _.union(updated, INCLUDES);
    updated = _.difference(updated, EXCLUDES);
  });
  return updated;
};

const auth = (state = initialState, action) => {
  switch (action.type) {
    case GET_USER_BY_OKTA_USER_ID: {
      const user = action.payload;
      let permissions = _.union(user.permissions, standardPermissions);

      const { roles } = user;
      permissions = applyPersonas(permissions, roles);

      const userClone = _.cloneDeep(user);
      userClone.ownerOrganization = userClone.organization;
      delete userClone.permissions;
      delete userClone.organization;

      return {
        ...state,
        user: userClone,
        permissions,
        retrievedUserInfo: 'loading',
      };
    }
    case formatSuccess(GET_USER_BY_OKTA_USER_ID): {
      return { ...state, retrievedUserInfo: SUCCESS };
    }
    case formatError(GET_USER_BY_OKTA_USER_ID): {
      // Health-check (bootstrap user lookup) failed. Decide whether this is a
      // genuine auth failure (sign the user out) or a transient one (keep the
      // session, show a retryable error) — the behavior HMB-19203 introduced to
      // stop backgrounded/blip-driven forced logouts.
      //
      // Payload shape: doRequest surfaces HTTP 401/403 as the bare string
      // 'UNAUTH' (serializeError passes strings through); other failures arrive
      // as a serialized object { name, status, statusCode }. Handle both.
      const rawError = action.payload?.error;
      const error = typeof rawError === 'object' ? rawError?.name : undefined;
      const errorCode =
        typeof rawError === 'object'
          ? (rawError?.status ?? rawError?.statusCode)
          : undefined;

      // NATIVE_AUTH_FAILURE (native token acquisition failed) is an auth failure
      // sentinel like UNAUTH — defer to the post-retry DEAUTH rather than
      // treating it as a transient outage / retryable screen.
      const isGenuineAuthFailure =
        rawError === 'UNAUTH' ||
        error === 'UNAUTH' ||
        rawError === 'NATIVE_AUTH_FAILURE' ||
        error === 'NATIVE_AUTH_FAILURE' ||
        errorCode === 401 ||
        errorCode === 403;

      if (isGenuineAuthFailure) {
        // Do NOT set `logout` here. doRequest dispatches this error BEFORE
        // apiCall runs its retry-before-deauth, so flipping `logout` now would
        // fire useLogoutHandler mid-retry and defeat the retry. The
        // authoritative sign-out for auth failures is the DEAUTH_USER action
        // apiCall dispatches AFTER the retry is exhausted (actions/api/index.js).
        // Leave state unchanged (still "loading") while the retry runs.
        return state;
      }

      if (error === 'failedToGetUser' && errorCode === 500) {
        // User genuinely could not be resolved (e.g. deprovisioned) -> sign out.
        return {
          ...state,
          logout: true,
          retrievedUserInfo: 'user-lookup-failure',
        };
      }

      // Don't let a late/overlapping transient error tear down an already-
      // bootstrapped session: an earlier overlapping getUserByOktaUserId may
      // have already reached SUCCESS, and a stale error arriving afterward must
      // not revert the working app to the retryable session-error screen.
      if (state.retrievedUserInfo === SUCCESS) {
        return state;
      }

      // Transient failure (network drop, timeout, 5xx, token hiccup on resume):
      // keep the session and show a retryable error instead of logging out.
      // Telemetry for this outcome is emitted (once) from secure-route on the
      // state transition — kept out of the reducer to preserve purity and avoid
      // double-firing (doRequest.catch + apiCall.fail both dispatch this error).
      return {
        ...state,
        retrievedUserInfo: 'health-check-transient-error',
      };
    }
    case formatInit(RESET_PASSWORD): {
      return { ...state, pendingRequest: true };
    }
    case formatSuccess(RESET_PASSWORD): {
      return {
        ...state,
        passwordResetted: true,
        error: false,
        pendingRequest: false,
      };
    }
    case formatSuccess(CHANGE_PASSWORD): {
      return {
        ...state,
        passwordIsChanged: true,
        error: false,
        pendingRequest: false,
      };
    }
    case AUTH_ERROR: {
      const error =
        action.payload && typeof action.payload === 'object'
          ? 'Auth error'
          : action.payload;
      return {
        ...state,
        error,
        pendingRequest: false,
        passwordIsChanged: false,
      };
    }
    case AUTH_USER: {
      const { user } = action.payload;
      // Transform organization to ownerOrganization for consistency
      const userForDatadog = user?.organization
        ? { ...user, ownerOrganization: user.organization }
        : user;
      setupDatadogRumUserAndContext(userForDatadog);
      return { ...state };
    }
    case SET_CURRENT_USER_ROLES: {
      const { roles } = action.payload;
      const permissions = applyPersonas(state.permissions, roles);
      return { ...state, user: { ...state.user, roles }, permissions };
    }
    case SET_CURRENT_USER_GROUPS: {
      const updatedState = {
        ...state,
        user: {
          ...state.user,
          groups: action.payload.groups,
          groupLeads: action.payload.groupLeads,
        },
      };
      if (process?.env?.ENABLE_THIRD_PARTY_SCRIPTS) {
        pendoHelpers.identify(updatedState?.user);
      }
      return updatedState;
    }
    case DEAUTH_USER: {
      return { ...state, logout: true };
    }
    case PUT_USER_AUTOASSIGN: {
      const isAutoAssign = _.get(
        action,
        'payload.isAutoAssign',
        state.user.isAutoAssign,
      );
      return {
        ...state,
        user: {
          ...state.user,
          isAutoAssign,
        },
      };
    }
    case SET_ACCESS_TOKEN_ERROR_MESSAGE: {
      return {
        ...state,
        accessTokenErrorMessage: action.payload,
      };
    }
    case UPDATE_PROFILE: {
      const updatedUser = action.payload;
      if (
        (updatedUser?._id || updatedUser?.id) ===
        (state?.user?._id || state?.user?.id)
      ) {
        return {
          ...state,
          user: { ...state.user, ...updatedUser },
        };
      }
      return state;
    }
    default: {
      return state;
    }
  }
};

export default auth;
